import { useState, useCallback, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/Dialog";
import Select from "@/components/ui/form/Select";
import { Calendar, Plus, X } from "lucide-react";
import { IEventFormData } from "@/types/ProductTypes";
import {
  ICreateEventStringPropertyPayload,
  ICreateEventBoolPropertyPayload,
  IEventResponse,
  IEventStringProperty,
} from "@/types/EventTypes";
import { V1BEClient } from "@/clients";
import { useAuthStore } from "@/store/authStore";
import { formatDateForInput } from "@/lib/utils";
import { toast } from "sonner";

interface EventUserDialogProps {
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  eventToEdit?: IEventResponse | null;
  onSuccess?: () => void;
}

export default function EventUserDialog({
  open = true,
  onOpenChange,
  eventToEdit,
  onSuccess,
}: EventUserDialogProps) {
  const { setUserEvents, setHasEvents, hasEvents } = useAuthStore();
  const [isLoading, setIsLoading] = useState(false);

  // Extended form data to include dynamic properties
  interface ExtendedEventFormData extends IEventFormData {
    bride_name?: string;
    groom_name?: string;
    place_of_origin?: string;
    birthday?: string;
    age?: number;
    company_name?: string;
    company_address?: string;
  }

  const [formData, setFormData] = useState<ExtendedEventFormData>({
    title: "",
    event_date: "",
    event_type: "",
    event_type_other: "",
    celebrants: [""],
    web_template: "template-3",
    bride_name: "",
    groom_name: "",
    place_of_origin: "",
    birthday: "",
    age: 0,
    company_name: "",
    company_address: "",
  });

  const [errors, setErrors] = useState<Partial<ExtendedEventFormData>>({
    title: "",
    event_date: "",
    event_type: "",
    event_type_other: "",
    celebrants: [""],
  });

  useEffect(() => {
    if (open) {
      if (eventToEdit) {
        // Editing existing event
        const properties = eventToEdit.event_string_properties || [];
        const getProperty = (name: string) =>
          properties.find((p) => p.name === name)?.value || "";

        // Convert celebrants from IEventCelebrant[] to string[]
        const celebrantsArray = eventToEdit.celebrants
          ? eventToEdit.celebrants.map((c) =>
              typeof c === "string" ? c : c.name
            )
          : [""];

        setFormData({
          title: eventToEdit.title || "",
          event_date: formatDateForInput(eventToEdit.event_date),
          event_type: getProperty("event_type"),
          event_type_other: getProperty("event_type_other"),
          celebrants: celebrantsArray.length > 0 ? celebrantsArray : [""],
          web_template: getProperty("web_template") || "template-3",
          bride_name: getProperty("bride_name"),
          groom_name: getProperty("groom_name"),
          place_of_origin: getProperty("place_of_origin"),
          birthday: getProperty("birthday"),
          age: parseInt(getProperty("age")) || 0,
          company_name: getProperty("company_name"),
          company_address: getProperty("company_address"),
        });
      } else {
        // Reset form for creation
        setFormData({
          title: "",
          event_date: "",
          event_type: "",
          event_type_other: "",
          celebrants: [""],
          web_template: "template-3",
          bride_name: "",
          groom_name: "",
          place_of_origin: "",
          birthday: "",
          age: 0,
          company_name: "",
          company_address: "",
        });
      }
      setErrors({});
    }
  }, [open, eventToEdit]);

  const eventTypes = [
    { value: "wedding", label: "Wedding" },
    { value: "debut", label: "Debut" },
    { value: "birthday", label: "Birthday" },
    { value: "corporate event", label: "Corporate Events" },
    { value: "others", label: "Others" },
  ];

  // Auto-calculate age when birthday changes
  const calculateAge = useCallback((birthDateString: string) => {
    if (!birthDateString) return 0;
    const today = new Date();
    const birthDate = new Date(birthDateString);
    let age = today.getFullYear() - birthDate.getFullYear();
    const m = today.getMonth() - birthDate.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    return age;
  }, []);

  const handleInputChange = useCallback(
    (field: keyof ExtendedEventFormData, value: string | number) => {
      setFormData((prev) => {
        const newData = { ...prev, [field]: value };

        // Auto-calculate age if birthday changes
        if (field === "birthday") {
          newData.age = calculateAge(value as string);
        }

        return newData;
      });

      // Clear error when user starts typing
      if (errors[field]) {
        setErrors((prev) => ({ ...prev, [field]: "" }));
      }
    },
    [errors, calculateAge]
  );

  const handleEventTypeChange = useCallback(
    (value: string) => {
      setFormData((prev) => ({ ...prev, event_type: value }));
      // Clear error when user starts typing
      if (errors.event_type) {
        setErrors((prev) => ({ ...prev, event_type: "" }));
      }
    },
    [errors.event_type]
  );

  const handleOtherEventTypeChange = useCallback(
    (value: string) => {
      setFormData((prev) => ({
        ...prev,
        event_type_other: value,
        // Don't update event_type here - we'll handle it in the submit
      }));
      // Clear error when user starts typing
      if (errors.event_type_other) {
        setErrors((prev) => ({ ...prev, event_type_other: "" }));
      }
    },
    [errors.event_type_other]
  );

  const handleCelebrantChange = useCallback((index: number, value: string) => {
    setFormData((prev) => ({
      ...prev,
      celebrants:
        prev.celebrants?.map((celebrant, i) =>
          i === index ? value : celebrant
        ) || [],
    }));
  }, []);

  const addCelebrant = useCallback(() => {
    setFormData((prev) => ({
      ...prev,
      celebrants: [...(prev.celebrants || []), ""],
    }));
  }, []);

  const removeCelebrant = useCallback(
    (index: number) => {
      if (formData.celebrants?.length && formData.celebrants.length > 1) {
        setFormData((prev) => ({
          ...prev,
          celebrants: prev.celebrants?.filter((_, i) => i !== index) || [],
        }));
      }
    },
    [formData.celebrants?.length]
  );

  const validateForm = useCallback(() => {
    const newErrors: Partial<ExtendedEventFormData> = {};

    if (!formData.title.trim()) {
      newErrors.title = "Event name is required";
    }

    if (!formData.event_date) {
      newErrors.event_date = "Event date is required";
    } else {
      const selectedDate = new Date(formData.event_date);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      if (selectedDate < today) {
        newErrors.event_date = "Event date cannot be in the past";
      }
    }

    if (!formData.event_type) {
      newErrors.event_type = "Event type is required";
    }

    if (
      formData.event_type === "others" &&
      !formData.event_type_other?.trim()
    ) {
      newErrors.event_type_other = "Please specify the event type";
    }

    // Conditional validations
    if (formData.event_type === "wedding") {
      if (!formData.bride_name?.trim())
        newErrors.bride_name = "Name of Bride is required";
      if (!formData.groom_name?.trim())
        newErrors.groom_name = "Name of Groom is required";
      if (!formData.place_of_origin?.trim())
        newErrors.place_of_origin = "Place of Origin is required";
    }

    if (formData.event_type === "birthday" || formData.event_type === "debut") {
      if (!formData.birthday) {
        newErrors.birthday = "Birthday is required";
      } else {
        const birthDate = new Date(formData.birthday);
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        if (birthDate >= today) {
          newErrors.birthday = "Birthday must be in the past";
        }
      }
      if (!formData.place_of_origin?.trim())
        newErrors.place_of_origin = "Place of Origin is required";
    }

    if (formData.event_type === "corporate event") {
      if (!formData.company_name?.trim())
        newErrors.company_name = "Name of Company is required";
      if (!formData.company_address?.trim())
        newErrors.company_address = "Address is required";
      // Note: "Name of Event" is mapped to formData.title, which is already required
    }

    // Only validate celebrants when event type is "others"
    if (formData.event_type === "others") {
      const validCelebrants =
        formData.celebrants?.filter((celebrant: string) => celebrant?.trim()) ||
        [];
      if (validCelebrants.length === 0) {
        newErrors.celebrants = ["At least one celebrant is required"];
      }
    }

    return newErrors;
  }, [formData]);

  const handleGenerateEventProperties = useCallback(
    (data: ExtendedEventFormData): IEventFormData => {
      const eventStringProperties: ICreateEventStringPropertyPayload[] = [];
      const eventBoolProperties: ICreateEventBoolPropertyPayload[] = [];

      // Event type to properties
      eventStringProperties.push({
        name: "event_type",
        value: data.event_type,
      });

      if (data.event_type === "others") {
        eventStringProperties.push({
          name: "event_type_other",
          value: data.event_type_other || "",
        });
      }

      // Add conditional properties
      if (data.event_type === "wedding") {
        if (data.bride_name)
          eventStringProperties.push({
            name: "bride_name",
            value: data.bride_name,
          });
        if (data.groom_name)
          eventStringProperties.push({
            name: "groom_name",
            value: data.groom_name,
          });
        if (data.place_of_origin)
          eventStringProperties.push({
            name: "place_of_origin",
            value: data.place_of_origin,
          });
      }

      if (data.event_type === "birthday" || data.event_type === "debut") {
        if (data.birthday)
          eventStringProperties.push({
            name: "birthday",
            value: data.birthday,
          });
        if (data.age !== undefined)
          eventStringProperties.push({
            name: "age",
            value: data.age.toString(),
          });
        if (data.place_of_origin)
          eventStringProperties.push({
            name: "place_of_origin",
            value: data.place_of_origin,
          });
      }

      if (data.event_type === "corporate event") {
        if (data.company_name)
          eventStringProperties.push({
            name: "company_name",
            value: data.company_name,
          });
        if (data.company_address)
          eventStringProperties.push({
            name: "company_address",
            value: data.company_address,
          });
      }

      // Web template
      eventStringProperties.push({
        name: "web_template",
        value: data.web_template,
      });

      return {
        ...data,
        event_string_properties: eventStringProperties,
        event_bool_properties: eventBoolProperties,
      };
    },
    []
  );

  const handleUpdateEventStringProperties = async (
    event: IEventResponse,
    data: ICreateEventStringPropertyPayload[],
    isForUpdate: boolean
  ): Promise<{
    stringProperties: IEventStringProperty[];
  }> => {
    // Delete properties
    if (isForUpdate) {
      if (event?.id && event?.event_string_properties) {
        const propsToDelete = event.event_string_properties
          .map((property) => property.id.toString())
          .join(",");
        if (propsToDelete) {
          await V1BEClient.deleteEventStringProperty(event.id, propsToDelete);
        }
      }
    }

    let stringProperties: IEventStringProperty[] = [];

    // Create properties
    if (data.length > 0) {
      const res = await V1BEClient.createEventStringProperty(event.id, data);

      if (res && event) {
        stringProperties = res;
      }
    }

    return {
      stringProperties,
    };
  };

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();

      const validationErrors = validateForm();
      if (Object.keys(validationErrors).length > 0) {
        setErrors(validationErrors);
        return;
      }

      setIsLoading(true);

      // Transform data using property generation
      const finalFormData = handleGenerateEventProperties(formData);

      try {
        if (eventToEdit) {
          // Update existing event
          const result = await V1BEClient.updateEvent(
            finalFormData,
            eventToEdit.id
          );
          if (result) {
            await handleUpdateEventStringProperties(
              result,
              finalFormData.event_string_properties || [],
              true
            );
            toast.success("Event updated successfully!");
            if (onSuccess) onSuccess();
            if (onOpenChange) onOpenChange(false);
          }
        } else {
          // Create new event
          const result = await V1BEClient.createEvent(finalFormData);
          if (result) {
            // Update the auth store to reflect that user now has events
            setUserEvents([finalFormData]); // Add the new event to the store
            setHasEvents(true);

            await handleUpdateEventStringProperties(
              result,
              finalFormData.event_string_properties || [],
              false
            );

            toast.success("Event created successfully!");
            if (onSuccess) onSuccess();
            if (onOpenChange) onOpenChange(false);
          }
        }
      } catch (error) {
        console.error("Error saving event:", error);
        toast.error("Failed to save event");
      } finally {
        setIsLoading(false);
      }
    },
    [
      formData,
      validateForm,
      handleGenerateEventProperties,
      setUserEvents,
      setHasEvents,
      eventToEdit,
      onSuccess,
      onOpenChange,
    ]
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        hideCloseButton={true}
        className="max-w-2xl md:max-h-[90vh] md:h-auto h-[100vh] overflow-y-auto bg-white"
      >
        <DialogHeader>
          <DialogTitle className="!text-orange-500 text-2xl font-bold text-gray-900 flex items-center justify-center gap-2">
            <Calendar className="w-6 h-6" />
            {eventToEdit ? "Edit Event" : "Create Your Event"}
          </DialogTitle>
          {!eventToEdit && !hasEvents && (
            <p className="text-gray-600 text-center">
              <strong>Welcome to PhilTrade! </strong>
              You haven't created an event yet. Let's get started by creating
              one!
            </p>
          )}
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Event Name */}
          <div>
            <label
              htmlFor="eventName"
              className="block text-sm font-medium text-gray-700 mb-2"
            >
              {formData.event_type === "corporate event"
                ? "Name of Event *"
                : "Event Name *"}
            </label>
            <input
              type="text"
              id="eventName"
              value={formData.title}
              onChange={(e) => handleInputChange("title", e.target.value)}
              className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-colors ${
                errors.title ? "border-red-300" : "border-gray-300"
              }`}
              placeholder="Enter event name"
            />
            {errors.title && (
              <p className="mt-1 text-sm text-red-600">{errors.title}</p>
            )}
          </div>

          {/* Event Date */}
          <div>
            <label
              htmlFor="event_date"
              className="block text-sm font-medium text-gray-700 mb-2"
            >
              Event Date *
            </label>
            <input
              type="date"
              id="event_date"
              value={formData.event_date}
              onChange={(e) => handleInputChange("event_date", e.target.value)}
              className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-colors ${
                errors.event_date ? "border-red-300" : "border-gray-300"
              }`}
            />
            {errors.event_date && (
              <p className="mt-1 text-sm text-red-600">{errors.event_date}</p>
            )}
          </div>

          {/* Event Type */}
          <div>
            <label
              htmlFor="event_type"
              className="block text-sm font-medium text-gray-700 mb-2"
            >
              Event Type *
            </label>
            <Select
              options={eventTypes}
              value={formData.event_type}
              onChange={(value) => handleEventTypeChange(value as string)}
              placeholder="Select event type"
              error={errors.event_type}
            />
          </div>

          {/* Wedding Fields */}
          {formData.event_type === "wedding" && (
            <div className="space-y-4 border-l-4 border-orange-200 pl-4 py-2">
              <h3 className="font-medium text-gray-900">Wedding Details</h3>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Name of Bride *
                </label>
                <input
                  type="text"
                  value={formData.bride_name}
                  onChange={(e) =>
                    handleInputChange("bride_name", e.target.value)
                  }
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-colors ${
                    errors.bride_name ? "border-red-300" : "border-gray-300"
                  }`}
                />
                {errors.bride_name && (
                  <p className="mt-1 text-sm text-red-600">
                    {errors.bride_name}
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Name of Groom *
                </label>
                <input
                  type="text"
                  value={formData.groom_name}
                  onChange={(e) =>
                    handleInputChange("groom_name", e.target.value)
                  }
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-colors ${
                    errors.groom_name ? "border-red-300" : "border-gray-300"
                  }`}
                />
                {errors.groom_name && (
                  <p className="mt-1 text-sm text-red-600">
                    {errors.groom_name}
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Place of Origin *
                </label>
                <input
                  type="text"
                  value={formData.place_of_origin}
                  onChange={(e) =>
                    handleInputChange("place_of_origin", e.target.value)
                  }
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-colors ${
                    errors.place_of_origin
                      ? "border-red-300"
                      : "border-gray-300"
                  }`}
                />
                {errors.place_of_origin && (
                  <p className="mt-1 text-sm text-red-600">
                    {errors.place_of_origin}
                  </p>
                )}
              </div>
            </div>
          )}

          {/* Birthday/Debut Fields */}
          {(formData.event_type === "birthday" ||
            formData.event_type === "debut") && (
            <div className="space-y-4 border-l-4 border-orange-200 pl-4 py-2">
              <h3 className="font-medium text-gray-900">
                {formData.event_type === "debut" ? "Debut" : "Birthday"} Details
              </h3>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Birthday *
                </label>
                <input
                  type="date"
                  value={formData.birthday}
                  onChange={(e) =>
                    handleInputChange("birthday", e.target.value)
                  }
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-colors ${
                    errors.birthday ? "border-red-300" : "border-gray-300"
                  }`}
                />
                {errors.birthday && (
                  <p className="mt-1 text-sm text-red-600">{errors.birthday}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Age
                </label>
                <input
                  type="number"
                  value={formData.age}
                  readOnly
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Place of Origin *
                </label>
                <input
                  type="text"
                  value={formData.place_of_origin}
                  onChange={(e) =>
                    handleInputChange("place_of_origin", e.target.value)
                  }
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-colors ${
                    errors.place_of_origin
                      ? "border-red-300"
                      : "border-gray-300"
                  }`}
                />
                {errors.place_of_origin && (
                  <p className="mt-1 text-sm text-red-600">
                    {errors.place_of_origin}
                  </p>
                )}
              </div>
            </div>
          )}

          {/* Corporate Event Fields */}
          {formData.event_type === "corporate event" && (
            <div className="space-y-4 border-l-4 border-orange-200 pl-4 py-2">
              <h3 className="font-medium text-gray-900">
                Corporate Event Details
              </h3>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Name of Company *
                </label>
                <input
                  type="text"
                  value={formData.company_name}
                  onChange={(e) =>
                    handleInputChange("company_name", e.target.value)
                  }
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-colors ${
                    errors.company_name ? "border-red-300" : "border-gray-300"
                  }`}
                />
                {errors.company_name && (
                  <p className="mt-1 text-sm text-red-600">
                    {errors.company_name}
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Address *
                </label>
                <input
                  type="text"
                  value={formData.company_address}
                  onChange={(e) =>
                    handleInputChange("company_address", e.target.value)
                  }
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-colors ${
                    errors.company_address
                      ? "border-red-300"
                      : "border-gray-300"
                  }`}
                />
                {errors.company_address && (
                  <p className="mt-1 text-sm text-red-600">
                    {errors.company_address}
                  </p>
                )}
              </div>
            </div>
          )}

          {/* Other Event Type */}
          {formData.event_type === "others" && (
            <div>
              <label
                htmlFor="otherEventType"
                className="block text-sm font-medium text-gray-700 mb-2"
              >
                Specify Event Type *
              </label>
              <input
                type="text"
                id="otherEventType"
                value={formData.event_type_other || ""}
                onChange={(e) => handleOtherEventTypeChange(e.target.value)}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-colors ${
                  errors.event_type_other ? "border-red-300" : "border-gray-300"
                }`}
                placeholder="Enter event type"
              />
              {errors.event_type_other && (
                <p className="mt-1 text-sm text-red-600">
                  {errors.event_type_other}
                </p>
              )}
            </div>
          )}

          {/* Celebrants - Only show when Event Type is "Others" */}
          {formData.event_type === "others" && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Celebrants *
              </label>
              <div className="space-y-3">
                {formData.celebrants?.map((celebrant, index) => (
                  <div key={index} className="flex items-center gap-2">
                    <div className="flex-1">
                      <input
                        type="text"
                        value={celebrant}
                        onChange={(e) =>
                          handleCelebrantChange(index, e.target.value)
                        }
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-colors"
                        placeholder={`Celebrant ${index + 1} name`}
                      />
                    </div>
                    {formData.celebrants?.length &&
                      formData.celebrants.length > 1 && (
                        <button
                          type="button"
                          onClick={() => removeCelebrant(index)}
                          className="p-2 text-red-500 hover:text-red-700 hover:bg-red-50 rounded-lg transition-colors"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      )}
                  </div>
                ))}
                <button
                  type="button"
                  onClick={addCelebrant}
                  className="flex items-center gap-2 text-orange-600 hover:text-orange-700 text-sm font-medium"
                >
                  <Plus className="w-4 h-4" />
                  Add Another Celebrant
                </button>
              </div>
              {errors.celebrants && (
                <p className="mt-1 text-sm text-red-600">
                  {errors.celebrants[0]}
                </p>
              )}
            </div>
          )}

          {/* Submit Button */}
          <div className="flex justify-end gap-2 pt-4 border-t">
            <button
              type="button"
              onClick={() => onOpenChange?.(false)}
              className="px-6 py-2 border border-gray-300 rounded-lg font-medium text-gray-700 hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isLoading}
              className="bg-orange-600 text-white px-6 py-2 rounded-lg font-medium hover:bg-orange-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {isLoading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  {eventToEdit ? "Updating Event..." : "Creating Event..."}
                </>
              ) : (
                <>
                  <Calendar className="w-4 h-4" />
                  {eventToEdit ? "Update Event" : "Create Event"}
                </>
              )}
            </button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
